import React, { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { Event } from '../types';
import PublicHeader from './PublicHeader';
import { formatClubDate, formatClubTime } from '../utils/clubTime';

const client = generateClient();

const PAGE_SIZE = 12;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return formatClubDate(date, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return formatClubTime(date, { hour: '2-digit', minute: '2-digit' });
};

type Filter = 'upcoming' | 'past' | 'all';

interface Props {
  onNavigateHome: () => void;
}

/**
 * Full events listing. Defaults to upcoming; can switch to past-only or
 * all. Paginates PAGE_SIZE at a time.
 */
const EventsPage: React.FC<Props> = ({ onNavigateHome }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await client.models.Event.list({
          filter: { isActive: { eq: true } },
          limit: 500,
        });
        setEvents((result.data || []) as Event[]);
      } catch (err) {
        console.error('Error loading events:', err);
        setError('Failed to load events. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const now = Date.now();
    const slice = events.filter((e) => {
      const t = new Date(e.eventDate).getTime();
      if (filter === 'upcoming') return t >= now;
      if (filter === 'past') return t < now;
      return true;
    });
    // Upcoming: nearest date first. Past/all: most recent first.
    slice.sort((a, b) => {
      const at = new Date(a.eventDate).getTime();
      const bt = new Date(b.eventDate).getTime();
      return filter === 'upcoming' ? at - bt : bt - at;
    });
    return slice;
  }, [events, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 whenever the filter changes.
  useEffect(() => {
    setPage(1);
  }, [filter]);

  return (
    <main className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <PublicHeader title="All Events" />

      <div className="max-w-5xl mx-auto py-12 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-fishing-blue mb-8">All Events</h1>

        <div className="mb-6 flex gap-2" role="tablist" aria-label="Filter events">
          {(['upcoming', 'past', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              role="tab"
              aria-selected={filter === f}
              className={`px-4 py-2 text-sm rounded-full capitalize transition-colors ${
                filter === f
                  ? 'bg-fishing-blue text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-600">Loading…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-600">
            {filter === 'upcoming' ? 'No upcoming events scheduled.' : 'No events found.'}
          </div>
        ) : (
          <>
            <ul className="space-y-4">
              {visible.map((e) => (
                <li
                  key={e.id}
                  className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h2 className="text-lg font-semibold text-fishing-blue">{e.title}</h2>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {e.eventType?.replace('_', ' ')}
                        </span>
                      </div>
                      {(e.shortDescription || e.description) && (
                        <p className="text-gray-600 text-sm mb-2">
                          {e.shortDescription || e.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span>📅 {formatDate(e.eventDate)}</span>
                        <span>
                          🕐 {formatTime(e.eventDate)}
                          {/* Rendered from the canonical competitionEndTime,
                              not the title text — see
                              .kiro/specs/competition-timing-canonical R5.3. */}
                          {e.eventType === 'COMPETITION' && e.competitionEndTime && (
                            <> – {formatTime(e.competitionEndTime)}</>
                          )}
                        </span>
                        {e.location && <span>📍 {e.location}</span>}
                        {e.cost && e.cost > 0 && (
                          <span className="text-fishing-green font-medium">
                            💵 ${e.cost} {e.currency || ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {totalPages > 1 && (
              <nav className="flex items-center justify-between mt-8" aria-label="Pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next →
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default EventsPage;
